import React, { useState, useEffect } from 'react';
import { Send, Play, ArrowLeft, MoreVertical, Paperclip, Mic, CheckCheck } from 'lucide-react';
import Button from '../ui/Button';
import { getTgLink } from '../../utils/telegram';

// Компонент кривых для паттерна (5 волн)
const BackgroundCurves: React.FC = () => (
  // Чтобы использовать переменные из globals.css в SVG, мы применяем стили через атрибут style
  <svg 
    className="absolute inset-0 w-full h-full pointer-events-none" 
    viewBox="0 0 1440 800" 
    xmlns="http://www.w3.org/2000/svg"
    preserveAspectRatio="none"
  >
    <defs>
      {/* Кривая линия 1: brand-primary с прозрачностью 10% */}
      <path id="curve1" d="M 0 500 C 300 300 600 700 900 500 S 1200 100 1440 300" fill="none" style={{ stroke: 'var(--brand-primary)', strokeOpacity: 0.10, strokeWidth: 2 }} />
      {/* Кривая линия 2: brand-primary с прозрачностью 5% */}
      <path id="curve2" d="M 0 300 C 200 500 400 100 700 300 S 1000 700 1440 500" fill="none" style={{ stroke: 'var(--brand-primary)', strokeOpacity: 0.05, strokeWidth: 2 }} />
      {/* Кривая линия 3: brand-primary с прозрачностью 3% */}
      <path id="curve3" d="M 0 650 C 450 450 850 600 1440 400" fill="none" style={{ stroke: 'var(--brand-primary)', strokeOpacity: 0.03, strokeWidth: 3 }} />
      {/* Кривая линия 4: brand-primary с прозрачностью 8% */}
      <path id="curve4" d="M 0 150 C 250 50 600 250 1440 100" fill="none" style={{ stroke: 'var(--brand-primary)', strokeOpacity: 0.08, strokeWidth: 1 }} />
      {/* Кривая линия 5: brand-primary с прозрачностью 12% (Дополнительная волна) */}
      <path id="curve5" d="M 0 750 C 150 200 350 780 700 350 S 1000 700 1040 250" fill="none" style={{ stroke: 'var(--brand-primary)', strokeOpacity: 0.12, strokeWidth: 1 }} />
      {/* Кривая линия 6: brand-primary с прозрачностью 8% (Дополнительная волна) */}
      <path id="curve4" d="M 0 150 C 250 50 600 250 1440 100" fill="none" style={{ stroke: 'var(--brand-primary)', strokeOpacity: 0.20, strokeWidth: 1 }} />
      {/* Кривая линия 7: brand-primary с прозрачностью 12% (Дополнительная волна) */}
      <path id="curve5" d="M 0 750 C 400 700 350 780 700 700 S 1100 700 1400 250" fill="none" style={{ stroke: 'var(--brand-primary)', strokeOpacity: 0.20, strokeWidth: 1 }} />
    </defs>
    <use href="#curve1" />
    <use href="#curve2" />
    <use href="#curve3" />
    <use href="#curve4" />
    <use href="#curve5" />
  </svg>
);


const LeadMagnet: React.FC = () => {
  // Состояния сцены
  const [sceneState, setSceneState] = useState(0);

  useEffect(() => {
    let timeouts: NodeJS.Timeout[] = [];

    const runSequence = () => {
      // Мгновенный сброс в начале цикла
      setSceneState(0);

      const sequence = [
        // 1. Появление сообщения пользователя
        { time: 500, action: () => setSceneState(1) },
        
        // 2. Ответ менеджера текстом
        { time: 2000, action: () => setSceneState(2) },
        
        // 3. Ответ менеджера видео
        { time: 3500, action: () => setSceneState(3) },
        
        // 4. СБРОС (Пауза)
        { time: 8500, action: () => runSequence() }, 
      ];

      sequence.forEach(step => {
        timeouts.push(setTimeout(step.action, step.time));
      });
    };

    runSequence();

    return () => timeouts.forEach(clearTimeout);
  }, []);

  return (
    // Фон секции: bg-brand-secondary. Добавлен relative для позиционирования паттерна.
    <section className="bg-brand-secondary py-24 lg:py-36 2xl:py-48 overflow-hidden text-brand-white relative sticky top">
      
      {/* ФОНОВЫЙ ПАТТЕРН */}
      <BackgroundCurves />

      {/* 1. Внешний DIV: ИСПРАВЛЕНО (Максимальная ширина и адаптивные отступы) */}
      <div className="w-full max-w-[1920px] mx-auto px-4 md:px-8 lg:px-12 xl:px-16 2xl:px-32 relative z-10">
        
        {/* 2. Родительский flex-контейнер: ВОЗВРАЩЕН В ПРЕДЫДУЩЕЕ СОСТОЯНИЕ */}
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-24">
          
          {/* Левая часть: Текст (Точная формула ширины Hero.tsx) */}
          <div className="w-full md:w-[calc(50%-10px)] lg:w-[calc(50%-12px)] xl:w-[calc(50%-16px)] 2xl:w-[calc(50%-20px)] relative z-10 text-center lg:text-left">
            
            <h2 className="display-sm md:display-md text-brand-white mb-6 leading-tight">
              Не уверены, что получится?
            </h2>
            
            {/* max-w-lg возвращен для контроля ширины текста на больших экранах */}
            <p className="body-base md:body-lg text-brand-white/50 mb-8 leading-relaxed lg:mx-0">
              Мы понимаем страх первого занятия. Поэтому записали 15-минутный видео-разбор базовых шагов. Попробуй дома в комфорте, а потом приходи к нам!
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Button variant="telegram" href={getTgLink("Привет! Хочу забрать мини-курс")}>
                Забрать в Telegram
              </Button>
            </div>
          </div>

          {/* Правая часть: Телефон (flex-1 и центрирование) */}
          <div className="w-full lg:flex-1 relative z-10 flex justify-center perspective-1000">
            
            {/* Корпус телефона: bg-brand-dark (#130C0F) + border-brand-secondary (#1c1c1c) */}
            <div className="relative w-72 md:w-80 bg-brand-dark border-[10px] border-brand-white rounded-[3rem] overflow-hidden aspect-[9/19] shadow-2xl">
              
              <div className="absolute inset-0 bg-brand-dark flex flex-col font-sans">
                
                {/* Header: bg-brand-secondary (#1c1c1c) */}
                <div className="bg-brand-secondary px-4 py-3 flex items-center justify-between shadow-sm z-20">
                  <div className="flex items-center gap-3">
                    <ArrowLeft size={20} className="text-brand-white/50" />
                    <div className="flex items-center gap-3">
                      {/* Аватар: bg-brand-primary. Градиент удален. */}
                      <div className="w-9 h-9 bg-brand-primary rounded-full flex items-center justify-center text-brand-white font-bold text-sm">
                        М
                      </div>
                      <div className="flex flex-col -gap-0.5">
                        <div className="text-brand-white text-sm font-semibold leading-none mb-1">Менеджер</div>
                        <div className="text-brand-telegram text-[11px] font-medium leading-none">в сети</div>
                      </div>
                    </div>
                  </div>
                  <MoreVertical size={20} className="text-brand-white/50" />
                </div>

                {/* Background Pattern в чате: Используем var(--brand-white) */}
                <div 
                  className="absolute inset-0 opacity-[0.03] pointer-events-none"
                  style={{
                    backgroundImage: `radial-gradient(var(--brand-white) 1px, transparent 1px)`,
                    backgroundSize: '20px 20px'
                  }}
                ></div>

                {/* Chat Area */}
                <div className="flex-1 p-3 flex flex-col gap-2 overflow-y-auto relative z-10 custom-scrollbar">
                  <div className="flex-1 min-h-0"></div> {/* Spacer */}

                  {/* 1. СООБЩЕНИЕ ПОЛЬЗОВАТЕЛЯ: bg-brand-primary (#F5300D) */}
                  {sceneState >= 1 && (
                    <div className="flex justify-end mb-2 animate-fade-in-up origin-bottom-right">
                      <div 
                        className="bg-brand-primary text-brand-white rounded-[18px] px-3 py-2 max-w-[85%] relative shadow-sm mr-[9px]"
                        style={{ borderBottomRightRadius: 0 }} 
                      >
                        <p className="text-[14px] leading-snug">Привет! Хочу забрать мини-курс</p>
                        <div className="flex justify-end items-center gap-1 mt-1">
                          <span className="text-[10px] text-brand-white/70">14:30</span>
                          {sceneState >= 2 && <CheckCheck size={12} className="text-brand-white/50" />}
                        </div>
                        
                        {/* Хвостик справа */}
                        <svg className="absolute -right-[9px] bottom-0 w-[9px] h-[15px] fill-brand-primary" viewBox="0 0 9 15">
                           <path d="M0,0 C0,0 3.5,6 9,15 L0,15 Z" />
                        </svg>
                      </div>
                    </div>
                  )}

                  {/* 2. ОТВЕТ МЕНЕДЖЕРА (ТЕКСТ): bg-brand-secondary (#1c1c1c) */}
                  {sceneState >= 2 && (
                    <div className="flex justify-start mb-2 animate-fade-in-up origin-bottom-left">
                      <div 
                        className="bg-brand-secondary text-brand-white rounded-[18px] px-3 py-2 max-w-[85%] relative shadow-sm ml-[9px]"
                        style={{ borderBottomLeftRadius: 0 }}
                      >
                        <p className="text-[14px] leading-snug">Привет! Лови видео с разбором базовых шагов!</p>
                        <div className="flex justify-end mt-1">
                            <span className="text-[10px] text-brand-white/50">14:31</span>
                        </div>
                        {/* Хвостик слева */}
                        <svg className="fill-brand-secondary absolute -left-[9px] bottom-0 w-[9px] h-[15px] transform scale-x-[-1]" viewBox="0 0 9 15">
                          <path d="M0,0 C0,0 3.5,6 9,15 L0,15 Z" />
                        </svg>
                      </div>
                    </div>
                  )}

                  {/* 3. ОТВЕТ МЕНЕДЖЕРА (ВИДЕО): bg-brand-secondary (#1c1c1c) */}
                  {sceneState >= 3 && (
                    <div className="flex justify-start mb-4 animate-fade-in-up origin-bottom-left">
                      <div 
                        className="bg-brand-secondary p-1 rounded-[18px] max-w-[85%] relative shadow-sm ml-[9px]"
                        style={{ borderBottomLeftRadius: 0 }}
                      >
                        <div className="relative rounded-xl overflow-hidden aspect-video group cursor-pointer bg-brand-dark">
                          <img 
                            src="/images/gallery/video-preview.png" 
                            className="w-full h-full object-cover opacity-80 transition-transform duration-500" 
                            alt="Preview"
                          />
                          {/* Оверлей: bg-brand-dark/50. */}
                          <div className="absolute inset-0 bg-brand-dark/50 flex items-center justify-center">
                            <div className="w-10 h-10 bg-brand-dark/50 rounded-full flex items-center justify-center pl-1 border border-brand-white/20">
                              <Play size={18} fill="white" className="text-brand-white"/>
                            </div>
                          </div>
                          {/* Длительность: bg-brand-secondary/70 */}
                          <div className="absolute bottom-2 left-2 px-2 py-0.5 bg-brand-secondary/70 rounded text-[10px] text-brand-white font-medium">
                            10:40
                          </div>
                        </div>
                        <div className="px-2 py-1.5 flex justify-between items-end">
                            <div>
                              <p className="text-[13px] font-medium leading-tight text-brand-white">Базовые шаги.mp4</p>
                              <p className="text-[11px] text-brand-white/50">65 MB</p>
                            </div>
                            <span className="text-[10px] text-brand-white/50">14:31</span>
                        </div>
                          {/* Хвостик слева */}
                          <svg className="fill-brand-secondary absolute -left-[9px] bottom-0 w-[9px] h-[15px] transform scale-x-[-1]" viewBox="0 0 9 15">
                            <path d="M0,0 C0,0 3.5,6 9,15 L0,15 Z" />
                          </svg>
                      </div>
                    </div>
                  )}
                </div>

                {/* Input Area: bg-brand-secondary (#1c1c1c) */}
                <div className="bg-brand-secondary px-3 py-3 z-20">
                  <div className="flex items-end gap-2">
                    <div className="w-10 h-10 flex items-center justify-center text-brand-white/50 mb-0.5">
                        <Paperclip size={18} />
                    </div>
                    {/* ПОЛЕ ВВОДА: bg-brand-dark (#130C0F) */}
                    <div className="flex-1 bg-brand-dark rounded-2xl px-4 py-2.5 min-h-[40px]">
                      <p className={`text-[15px] ${sceneState === 0 ? 'text-brand-white' : 'text-brand-white/50'}`}>
                        {sceneState === 0 ? "Привет! Хочу забрать мини-курс" : "Сообщение..."}
                      </p>
                    </div>
                    {/* КНОПКА: bg-brand-primary (#F5300D) */}
                    { sceneState === 0 ? (
                        <div className="w-10 h-10 bg-brand-primary rounded-full flex items-center justify-center text-brand-white mb-0.5 animate-pulse transition-all">
                            <Send size={24} />
                        </div>
                    ) : (
                        <div className="w-10 h-10 bg-brand-secondary rounded-full flex items-center justify-center text-brand-white/50 mb-0.5">
                            <Mic size={18} />
                        </div>
                    )}
                  </div>
                </div>

              </div>
            </div>
            
          </div>
        </div>

      </div>

      {/* Стили анимации и скроллбара оставлены без изменений */}
      <style>{`
        @keyframes fade-in-up {
          from { opacity: 0; transform: translateY(10px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 0px;
          background: transparent;
        }
      `}</style>
    </section>
  );
};

export default LeadMagnet;